import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useStore } from '../../../state/zustand-store';
import { removeIpIntrusionRules, UserKeys } from '../../../../api/users-api';
import { IpIntrusionRule } from '../../../../../types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteIpIntrusionAddressesDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  changeLogRoles: string[];
}

export default function DeleteIpIntrusionAddressesDialog({
  open,
  handleClose,
  selectedRows,
  changeLogRoles,
}: DeleteIpIntrusionAddressesDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const deletedAddresses: IpIntrusionRule[] = selectedRows.map((item) => {
    const [type, value] = item.split('||').map((part) => part.trim());
    return { type, value };
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      removeIpIntrusionRules(deletedAddresses, changeLogMessage),
    onSuccess: (updateIpSettings) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'Removed IP Intrusion address successfully!'
            : 'Removed IP Intrusion addresses successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.IpIntrusionSettings],
        updateIpSettings
      );
      setChangeLogMessage('');

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue removing the IP Intrusion address. Please refresh and try again.'
            : 'There was an issue removing the IP Intrusion addresses. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.administration.settings.remove_ipintrusion_addresses,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.DeleteIpIntrusionEntity.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedRows.length === 1
          ? 'Remove IP Intrusion Address'
          : 'Remove IP Intrusion Addresses'
      }
      dialogContent={
        <Box>
          <Typography>
            {selectedRows.length === 1 ? (
              <>
                Do you wish to remove the IP Intrusion address{' '}
                <strong>{deletedAddresses[0].value}</strong>?
              </>
            ) : (
              <>
                Do you wish to remove <strong>{selectedRows.length}</strong> IP
                Intrusion addresses?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title:
          selectedRows.length === 1 ? 'Remove Address' : 'Remove Addresses',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
